import { MGPOptional, Set, MGPValidation, Utils } from '@everyboard/lib';
import { CoordSet } from '../../jscaip/CoordSet';
import { HexaDirection } from '../../jscaip/HexaDirection';
import { HexagonalUtils } from '../../jscaip/HexagonalUtils';
import { HiveFailure } from './HiveFailure';
import { HiveCoordToCoordMove, HiveSpiderMove } from './HiveMove';
import { HivePieceStack } from './HivePiece';
export class HivePieceRules {
    static INSTANCES = MGPOptional.empty();
    static of(piece) {
        if (HivePieceRules.INSTANCES.isAbsent()) {
            HivePieceRules.INSTANCES = MGPOptional.of({
                'QueenBee': HiveQueenBeeRules.get(),
                'Beetle': HiveBeetleRules.get(),
                'Grasshopper': HiveGrasshopperRules.get(),
                'Spider': HiveSpiderRules.get(),
                'SoldierAnt': HiveSoldierAntRules.get(),
            });
        }
        return HivePieceRules.INSTANCES.get()[piece.kind];
    }
    checkEmptyDestination(move, state) {
        if (state.getAt(move.getEnd()).hasPieces()) {
            return MGPValidation.failure(HiveFailure.THIS_PIECE_CANNOT_CLIMB());
        }
        return MGPValidation.SUCCESS;
    }
    canSlideBetweenNeighbors(state, start, end) {
        // For the piece to slide from start to end,
        // one of the two common neighbors of start and end coord should be empty
        const startNeighbors = new CoordSet(HexagonalUtils.getNeighbors(start));
        const endNeighbors = new CoordSet(HexagonalUtils.getNeighbors(end));
        const commonNeighbors = startNeighbors.intersection(endNeighbors);
        for (const neighbor of commonNeighbors) {
            if (state.getAt(neighbor).isEmpty()) {
                return true;
            }
        }
        return false;
    }
}
export class HiveQueenBeeRules extends HivePieceRules {
    static INSTANCE = MGPOptional.empty();
    static get() {
        if (this.INSTANCE.isAbsent()) {
            this.INSTANCE = MGPOptional.of(new HiveQueenBeeRules());
        }
        return this.INSTANCE.get();
    }
    moveValidity(move, state) {
        if (HexagonalUtils.areNeighbors(move.getStart(), move.getEnd()) === false) {
            return MGPValidation.failure(HiveFailure.QUEEN_BEE_CAN_ONLY_MOVE_TO_DIRECT_NEIGHBORS());
        }
        if (this.canSlideBetweenNeighbors(state, move.getStart(), move.getEnd()) === false) {
            return MGPValidation.failure(HiveFailure.MUST_BE_ABLE_TO_SLIDE());
        }
        return this.checkEmptyDestination(move, state);
    }
    getPotentialMoves(coord, state) {
        const moves = [];
        for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
            if (state.getAt(neighbor).isEmpty()) {
                moves.push(HiveCoordToCoordMove.from(coord, neighbor).get());
            }
        }
        return moves;
    }
}
export class HiveBeetleRules extends HivePieceRules {
    static INSTANCE = MGPOptional.empty();
    static get() {
        if (this.INSTANCE.isAbsent()) {
            this.INSTANCE = MGPOptional.of(new HiveBeetleRules());
        }
        return this.INSTANCE.get();
    }
    moveValidity(move, state) {
        if (HexagonalUtils.areNeighbors(move.getStart(), move.getEnd()) === false) {
            return MGPValidation.failure(HiveFailure.BEETLE_CAN_ONLY_MOVE_TO_DIRECT_NEIGHBORS());
        }
        return MGPValidation.SUCCESS;
    }
    getPotentialMoves(coord, state) {
        const moves = [];
        for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
            moves.push(HiveCoordToCoordMove.from(coord, neighbor).get());
        }
        return moves;
    }
}
export class HiveGrasshopperRules extends HivePieceRules {
    static INSTANCE = MGPOptional.empty();
    static get() {
        if (this.INSTANCE.isAbsent()) {
            this.INSTANCE = MGPOptional.of(new HiveGrasshopperRules());
        }
        return this.INSTANCE.get();
    }
    moveValidity(move, state) {
        const direction = HexaDirection.factory.fromMove(move.getStart(), move.getEnd());
        if (direction.isFailure()) {
            return MGPValidation.failure(HiveFailure.GRASSHOPPER_MUST_MOVE_IN_STRAIGHT_LINE());
        }
        const jumpedCoords = move.getJumpedOverCoords();
        if (jumpedCoords.length === 0) {
            return MGPValidation.failure(HiveFailure.GRASSHOPPER_MUST_JUMP_OVER_PIECES());
        }
        for (const coord of jumpedCoords) {
            if (state.getAt(coord).isEmpty()) {
                return MGPValidation.failure(HiveFailure.GRASSHOPPER_MUST_JUMP_OVER_PIECES());
            }
        }
        return this.checkEmptyDestination(move, state);
    }
    getPotentialMoves(coord, state) {
        const moves = [];
        for (const direction of HexaDirection.factory.all) {
            const neighbor = coord.getNext(direction);
            if (state.getAt(neighbor).hasPieces()) {
                // We can jump in that direction
                let end = neighbor;
                while (state.getAt(end).hasPieces()) {
                    end = end.getNext(direction);
                }
                moves.push(HiveCoordToCoordMove.from(coord, end).get());
            }
        }
        return moves;
    }
}
export class HiveSpiderRules extends HivePieceRules {
    static INSTANCE = MGPOptional.empty();
    static get() {
        if (this.INSTANCE.isAbsent()) {
            this.INSTANCE = MGPOptional.of(new HiveSpiderRules());
        }
        return this.INSTANCE.get();
    }
    prefixLegality(coords, state) {
        let visited = new CoordSet();
        const stateWithoutMovedSpider = state.update()
            .setAt(coords[0], HivePieceStack.EMPTY)
            .increaseTurnAndFinalizeUpdate();
        for (let i = 1; i < coords.length; i++) {
            if (stateWithoutMovedSpider.getAt(coords[i]).hasPieces()) {
                return MGPValidation.failure(HiveFailure.THIS_PIECE_CANNOT_CLIMB());
            }
            if (HexagonalUtils.areNeighbors(coords[i - 1], coords[i]) === false) {
                return MGPValidation.failure(HiveFailure.SPIDER_MUST_MOVE_ON_NEIGHBORING_SPACES());
            }
            if (stateWithoutMovedSpider.haveCommonNeighbor(coords[i], coords[i - 1]) === false) {
                return MGPValidation.failure(HiveFailure.SPIDER_CAN_ONLY_MOVE_WITH_DIRECT_CONTACT());
            }
            if (this.canSlideBetweenNeighbors(stateWithoutMovedSpider, coords[i - 1], coords[i]) === false) {
                return MGPValidation.failure(HiveFailure.MUST_BE_ABLE_TO_SLIDE());
            }
            if (visited.contains(coords[i])) {
                return MGPValidation.failure(HiveFailure.SPIDER_CANNOT_BACKTRACK());
            }
            visited = visited.addElement(coords[i]);
        }
        return MGPValidation.SUCCESS;
    }
    moveValidity(move, state) {
        Utils.assert(move instanceof HiveSpiderMove, 'HiveSpiderRules: move should be a spider move');
        const spiderMove = move;
        const prefixLegality = this.prefixLegality(spiderMove.coords, state);
        if (prefixLegality.isFailure()) {
            return prefixLegality;
        }
        return this.checkEmptyDestination(move, state);
    }
    getPotentialMoves(coord, state) {
        const stateWithoutMovedSpider = state.update()
            .setAt(coord, HivePieceStack.EMPTY)
            .increaseTurnAndFinalizeUpdate();
        let movesSoFar = [[coord]];
        for (let i = 0; i < 3; i++) {
            movesSoFar = movesSoFar.flatMap((move) => this.nextMoveStep(stateWithoutMovedSpider, move));
        }
        function makeMove(move) {
            return HiveSpiderMove.ofCoords(move);
        }
        const uniqueMoves = new Set(movesSoFar.map(makeMove));
        return uniqueMoves.toList();
    }
    nextMoveStep(state, move) {
        const lastCoord = move[move.length - 1];
        function neighborsFilter(coord) {
            if (state.getAt(coord).hasPieces()) {
                // We can only go through empty spaces
                return false;
            }
            if (move.find((c) => coord.equals(c)) !== undefined) {
                // We cannot backtrack
                return false;
            }
            // Must have a common neighbor with the previous coord
            return state.haveCommonNeighbor(coord, lastCoord);
        }
        const possibleNeighbors = new CoordSet(HexagonalUtils.getNeighbors(lastCoord)).filter(neighborsFilter);
        return possibleNeighbors.toList().map((coord) => [...move, coord]);
    }
}
export class HiveSoldierAntRules extends HivePieceRules {
    static INSTANCE = MGPOptional.empty();
    static get() {
        if (this.INSTANCE.isAbsent()) {
            this.INSTANCE = MGPOptional.of(new HiveSoldierAntRules());
        }
        return this.INSTANCE.get();
    }
    pathExists(state, start, end) {
        let visited = new CoordSet();
        const worklist = [start];
        while (worklist.length > 0) {
            const coord = worklist.pop();
            if (visited.contains(coord)) {
                continue;
            }
            visited = visited.addElement(coord);
            if (coord.equals(end)) {
                return true;
            }
            for (const neighbor of HexagonalUtils.getNeighbors(coord)) {
                const isEmpty = state.getAt(neighbor).isEmpty();
                const hasOccupiedNeighbors = state.getOccupiedNeighbors(neighbor).size() > 0;
                const canSlide = this.canSlideBetweenNeighbors(state, coord, neighbor);
                if (isEmpty && hasOccupiedNeighbors && canSlide) {
                    worklist.push(neighbor);
                }
            }
        }
        return false;
    }
    moveValidity(move, state) {
        if (this.pathExists(state, move.getStart(), move.getEnd()) === false) {
            return MGPValidation.failure(HiveFailure.MUST_BE_ABLE_TO_SLIDE());
        }
        return this.checkEmptyDestination(move, state);
    }
    getPotentialMoves(coord, state) {
        let moves = new Set();
        for (const occupiedSpace of state.occupiedSpaces()) {
            if (occupiedSpace.equals(coord)) {
                // We will move so we don't look at this space
                continue;
            }
            for (const unoccupied of state.emptyNeighbors(occupiedSpace)) {
                moves = moves.addElement(HiveCoordToCoordMove.from(coord, unoccupied).get());
            }
        }
        return moves.toList();
    }
}
